import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { select, Store } from '@ngrx/store';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmDialogData } from '@shared/models/confirm-dialog-data';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@shared/services/animations/route.animations';
import { combineLatest, Subscription } from 'rxjs';
import { authSelector } from '@core/services/auth/state/authentication.selectors';

import { User } from '../models/user.model';
import { UserStoreService } from '../services/user-store.service';
import { usersLoad } from '../store/user.actions';
import { UserState } from '../store/user.reducers';
import { userSelector } from '../store/user.selectors';
import { UserAddComponent } from './user-add/user-add.component';
import { UserEditComponent } from './user-edit/user-edit.component';
import { AuthService } from '@core/services/auth/services/auth.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
})
export class UserManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  headElements = ['firstName', 'lastName', 'roles', 'actions'];
  isMobile = false;
  sub$: Subscription = new Subscription();
  dataSource = new MatTableDataSource<User>();
  currentUserEmail: string = '';
  currentUserIsWarden = false;
  allUsers: User[] = [];
  roleOptions: string[] = [];
  firstNameFilter = '';
  lastNameFilter = '';
  roleFilter = '';

  constructor(
    private store: Store<UserState>,
    private dialog: MatDialog,
    private userService: UserStoreService,
    private authservice: AuthService) {
       this.currentUserEmail = this.authservice.getUserEmail() ?? '';
    }

  ngOnInit(): void {
    this.store.dispatch(usersLoad());
    this.sub$.add(
      combineLatest([
        this.store.pipe(select(userSelector.allUsers)),
        this.store.pipe(select(authSelector.isWarden))
      ]).subscribe(([users, isWarden]) => {
        this.currentUserIsWarden = isWarden;
        this.allUsers = isWarden ? users : users.filter(u => !u.isWarden);
        this.roleOptions = Array.from(new Set(this.allUsers.map(user => this.getRoleLabel(user)))).sort();
        this.applyFilters();
      })
    );
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) => data[sortHeaderId].toLocaleLowerCase();
  }

  updateFirstNameFilter(value: string): void {
    this.firstNameFilter = value;
    this.applyFilters();
  }

  updateLastNameFilter(value: string): void {
    this.lastNameFilter = value;
    this.applyFilters();
  }

  updateRoleFilter(value: string): void {
    this.roleFilter = value ?? '';
    this.applyFilters();
  }

  getRoleLabel(user: User): string {
    return user.claims?.[0]?.value ?? '';
  }

  private applyFilters(): void {
    const firstName = this.firstNameFilter.trim().toLowerCase();
    const lastName = this.lastNameFilter.trim().toLowerCase();
    const role = this.roleFilter.trim().toLowerCase();

    this.dataSource.data = this.allUsers.filter(user => {
      const userFirstName = user.userData.firstName?.toLowerCase() ?? '';
      const userLastName = user.userData.lastName?.toLowerCase() ?? '';
      const userRole = this.getRoleLabel(user).toLowerCase();

      return (!firstName || userFirstName.includes(firstName))
        && (!lastName || userLastName.includes(lastName))
        && (!role || userRole === role);
    });

    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  add(): void {
    this.dialog.open(UserAddComponent);
  }

  edit(data: User): void {
    this.dialog.open(UserEditComponent, { data });
  }

  remove(user: User): void {
    const fullName = `${user.userData.firstName} ${user.userData.lastName}`;

    const data: ConfirmDialogData = {
      title: 'Remove System User',
      description: `Are you sure you would like to remove ${fullName} from the IRB system?`
    };
    this.sub$.add(
      this.dialog.open(ConfirmationDialogComponent, { data }).afterClosed()
        .subscribe(res => {
          if (res) {
            const role = user.isWarden ? 'Warden' : (user.isAdmin ? 'Admin' : 'Reader');
            this.userService.removeUserRole(user.userData.userName, role);
          }
        })
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }
}
